import React from "react";
import { ArrowRight, Play, Shield, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { HERO_IMAGE } from "../data/mock";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { useTranslation } from "react-i18next";

export const HeroSection = () => {
  const { t } = useTranslation();
  return (
    <section id="inicio" className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt={t("hero.imageAlt")}
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0C2340]/95 via-[#0C2340]/85 to-[#0C2340]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C2340]/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 mb-8 animate-fade-in-up">
            <Shield className="w-4 h-4 text-[#C8A24D]" />
            <span className="text-sm text-white/90 font-medium">{t("hero.badge")}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6 animate-fade-in-up delay-100" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {t("hero.title1")}{" "}
            <span className="relative">
              <span className="text-[#E8D5A0]">{t("hero.titleHighlight")}</span>
              <span className="absolute -bottom-1 left-0 w-full h-[3px] bg-[#C8A24D]/40 rounded-full" />
            </span>{" "}
            {t("hero.title2")}
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-10 max-w-2xl animate-fade-in-up delay-200" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("hero.subtitle")}{" "}
            <span className="text-[#E8D5A0] font-semibold">{t("hero.price")}</span>.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
            <Button
              size="lg"
              className="bg-[#C8A24D] hover:bg-[#B8920D] text-[#0C2340] font-semibold h-14 px-8 text-base rounded-xl shadow-xl shadow-[#C8A24D]/25 transition-all duration-300 hover:shadow-2xl hover:shadow-[#C8A24D]/30 group"
              onClick={() => document.getElementById('viabilidad')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t("hero.ctaPrimary")}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white/25 text-white bg-white/5 hover:bg-white/10 hover:text-white hover:border-white/40 h-14 px-8 text-base rounded-xl backdrop-blur-sm group"
                >
                  <Play className="w-4 h-4 mr-2 text-[#C8A24D]" />
                  {t("hero.ctaSecondary")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[900px] p-0 bg-black border-white/10 overflow-hidden">
                <DialogTitle className="sr-only">{t("hero.videoTitle")}</DialogTitle>
                <div className="aspect-video w-full">
                  <iframe
                    src="https://fast.wistia.net/embed/iframe/7rzwvpru9d?web_component=true&seo=true&autoPlay=true"
                    title={t("hero.videoAlt")}
                    allow="autoplay; fullscreen"
                    allowtransparency="true"
                    frameBorder="0"
                    scrolling="no"
                    name="wistia_embed"
                    className="w-full h-full"
                    allowFullScreen
                  ></iframe>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Quick Trust Indicators */}
          <div className="flex flex-wrap gap-6 mt-12 animate-fade-in-up delay-400">
            {[
              t("hero.trust1"),
              t("hero.trust2"),
              t("hero.trust3"),
            ].map((item) => (
              <span key={item} className="flex items-center gap-2 text-sm text-white/70">
                <CheckCircle2 className="w-4 h-4 text-[#C8A24D]" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#FAFAF7] to-transparent z-10" />
    </section>
  );
};

export default HeroSection;
